import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { PlayCircle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { streamSse } from "@/lib/sse";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  useDeviceReplacementScenarios,
  type DeviceReplacementProgress,
  type DeviceReplacementResult,
} from "@/lib/testDeviceReplacement";
import { Field, ProgressSteps, Row, TestHttpLogs, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

export function TestDeviceReplacementPage() {
  const { t } = useTranslation();
  const scenarios = useDeviceReplacementScenarios();

  const [scenario, setScenario] = useState("");
  const [tokenId, setTokenId] = useState("");
  const [message, setMessage] = useState("");
  const [autoProcess, setAutoProcess] = useState(true);
  const [autoComplete, setAutoComplete] = useState(false);
  const [simulateQrDelay, setSimulateQrDelay] = useState(false);
  const [qrDelayMs, setQrDelayMs] = useState("2000");

  const [steps, setSteps] = useState<DeviceReplacementProgress[]>([]);
  const [qrCodes, setQrCodes] = useState<string[]>([]);
  const [result, setResult] = useState<DeviceReplacementResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedData, setFailedData] = useState<DeviceReplacementProgress["data"] | null>(null);
  const [running, setRunning] = useState(false);

  const applyScenario = (name: string) => {
    setScenario(name);
    const s = scenarios.data?.find((x) => x.name === name);
    if (!s) return;
    setTokenId(s.tokenId);
    setMessage(s.message ?? "");
    setAutoProcess(s.autoProcess);
    setAutoComplete((s as { autoComplete?: boolean }).autoComplete ?? false);
    setSimulateQrDelay(s.simulateQrDelay);
  };

  const start = async () => {
    setError(null);
    if (!tokenId) {
      setError(t("testing.dr.tokenRequired"));
      return;
    }
    setSteps([]);
    setQrCodes([]);
    setResult(null);
    setRunning(true);

    const body = {
      tokenId,
      message: message || null,
      autoProcess,
      autoComplete,
      simulateQrDelay,
      qrDelayMs: parseInt(qrDelayMs, 10) || 2000,
    };

    try {
      await streamSse("/admin/api/v1/testing/device-replacement/start", body, (ev) => {
        if (!ev.data) return; // tento stream posílá jen `data:` (bez `event:`)
        const progress = JSON.parse(ev.data) as DeviceReplacementProgress;
        setSteps((s) => [...s, progress]);
        if (progress.status === "PROCESSED" && progress.data?.qrCodes) setQrCodes(progress.data.qrCodes);
        if (progress.status === "COMPLETED" && progress.data) setResult(progress.data as DeviceReplacementResult);
        if (progress.status === "FAILED") { setError(progress.message); setFailedData(progress.data ?? null); }
      });
    } catch (e) {
      setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("testing.dr.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("testing.dr.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t("testing.activation.params")}</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("testing.dr.scenario")}>
            <AppSelect value={scenario} onChange={(e) => applyScenario(e.target.value)}>
              <option value="">{t("testing.dr.scenarioNone")}</option>
              {(scenarios.data ?? []).map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label={`${t("testing.dr.tokenId")} *`}>
            <input className={inputCls} value={tokenId} onChange={(e) => setTokenId(e.target.value)} />
          </Field>
          <Field label={t("testing.dr.message")}>
            <input className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} />
          </Field>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={autoProcess} onChange={(e) => setAutoProcess(e.target.checked)} />
            {t("testing.dr.autoProcess")}
          </label>
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={autoComplete} onChange={(e) => setAutoComplete(e.target.checked)} />
            {t("testing.dr.autoComplete")}
          </label>
          <label className="flex items-center gap-2 text-sm text-primary">
            <input type="checkbox" checked={simulateQrDelay} onChange={(e) => setSimulateQrDelay(e.target.checked)} />
            {t("testing.dr.simulateQrDelay")}
          </label>
          {simulateQrDelay && (
            <label className="flex items-center gap-2 text-sm text-secondary">
              {t("testing.dr.qrDelayMs")}
              <input
                className={`${inputCls} w-28`}
                type="number"
                min={0}
                value={qrDelayMs}
                onChange={(e) => setQrDelayMs(e.target.value)}
              />
            </label>
          )}
        </div>

        <div>
          <Button size="lg" iconLeading={PlayCircle} isDisabled={running} onPress={() => void start()}>
            {running ? t("testing.running") : t("testing.dr.start")}
          </Button>
        </div>
      </div>

      <ProgressSteps steps={steps} />

      {/* QR sekvence */}
      {qrCodes.length > 0 && (
        <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <h2 className="text-md font-semibold text-primary">{t("testing.dr.qrCodes")}</h2>
          {qrCodes.map((qr, i) => (
            <div key={i} className="rounded-lg bg-secondary p-3 ring-1 ring-secondary">
              <div className="mb-1 text-xs font-semibold text-tertiary">
                {t("testing.dr.qrSequence", { index: i + 1, total: qrCodes.length })}
              </div>
              <pre className="max-h-48 overflow-auto rounded-md bg-primary p-2 font-mono text-xs break-all whitespace-pre-wrap text-secondary ring-1 ring-secondary">
                {qr}
              </pre>
            </div>
          ))}
          <p className="text-xs text-tertiary">{t("testing.dr.qrHelp")}</p>
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <h2 className="text-md font-semibold text-success-primary">
            ✓ {result.success ? t("testing.dr.success") : t("testing.dr.completedWarn")}
          </h2>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Row label={t("testing.dr.originalTokenId")} value={result.originalTokenId} mono />
            <Row label={t("testing.dr.transactionId")} value={result.transactionId} mono />
            <Row label={t("testing.dr.qrSequenceCount")} value={String(result.qrSequenceCount ?? 0)} />
            <Row label={t("testing.dr.newActivationGuid")} value={result.newActivationGuid || "—"} mono />
            {result.newTokenId && <Row label={t("testing.dr.newTokenId")} value={result.newTokenId} mono />}
            <Row label={t("testing.duration")} value={`${result.durationMs} ms`} />
            <Row label={t("testing.completedAt")} value={formatDateTime(result.completedAt)} />
            <Row label={t("testing.httpCalls")} value={String(result.httpLogs?.length ?? 0)} />
          </dl>

          {result.stepsExecuted && result.stepsExecuted.length > 0 && (
            <div>
              <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("testing.dr.stepsExecuted")}</h4>
              <ul className="list-inside list-disc text-sm text-secondary">
                {result.stepsExecuted.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Link
              to="/tokens/$tokenId"
              params={{ tokenId: result.originalTokenId }}
              className="rounded-lg bg-brand-solid px-3 py-2 text-sm font-semibold text-white hover:bg-brand-solid_hover"
            >
              {t("testing.dr.viewOriginal")}
            </Link>
            {result.newTokenId && (
              <Link
                to="/tokens/$tokenId"
                params={{ tokenId: result.newTokenId }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
              >
                {t("testing.dr.viewNew")}
              </Link>
            )}
            <a
              href={`/admin/api/v1/testing/device-replacement/export/${encodeURIComponent(result.originalTokenId)}?format=json`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportJson")}
            </a>
            <a
              href={`/admin/api/v1/testing/device-replacement/export/${encodeURIComponent(result.originalTokenId)}?format=csv`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportCsv")}
            </a>
          </div>
        </div>
      )}

      {error && !result && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-error">
          <h2 className="text-md font-semibold text-error-primary">✗ {t("testing.dr.failed")}</h2>
          <p className="mt-1 text-sm text-secondary">{error}</p>
          {failedData?.error && (
            <p className="mt-1 text-xs text-tertiary">{t("testing.dr.errorType")}: <span className="font-mono">{failedData.error}</span></p>
          )}
          {failedData?.stepsExecuted && failedData.stepsExecuted.length > 0 && (
            <div className="mt-2">
              <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("testing.dr.stepsBeforeFailure")}</h4>
              <ol className="list-decimal space-y-0.5 pl-5 text-xs text-secondary">
                {failedData.stepsExecuted.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {result && <TestHttpLogs logs={result.httpLogs ?? []} />}
    </div>
  );
}
